import { Injectable } from '@angular/core';
import { HttpContextToken, HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
 
import { ApiService, SILENT_HTTP_ERROR } from './api.service';
import { ToastController } from '@ionic/angular';

const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    alertService: any;
    constructor(private apiService:ApiService, private toastController: ToastController) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

       
        setTimeout(()=>{                           // <<<---using ()=> syntax
       // this.apiService.globalloading=false;
       // console.log('here');
        }, 500);

        return next.handle(request).pipe(catchError(err => {
            console.log(err);

            const expiredMessage = (err?.errors ?? err?.error?.errors)?.[0]?.message;
            if (this.shouldRefreshExpiredToken(request, expiredMessage)) {
                return this.apiService.refresh_token(true).pipe(
                    switchMap(() => {
                        const token = this.apiService.access_token || localStorage.getItem('access_token') || '';
                        const retry = request.clone({
                            context: request.context.set(RETRIED_AFTER_REFRESH, true),
                            setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
                        });
                        return next.handle(retry);
                    }),
                    catchError((secondErr) => {
                        if (String(secondErr?.url || '').includes('/auth/refresh')) {
                            this.apiService.present_toast('error', 'Session Expired.');
                            return throwError(err.error);
                        }
                        return this.presentRequestError(request, secondErr);
                    })
                );
            }

            return this.presentRequestError(request, err);
        }))
    }

    /** Same banner rules as before. A renewed request uses this only if that request still fails. */
    private presentRequestError(request: HttpRequest<any>, err: any): Observable<never> {
            let errormsg="Something went wrong, Please wait for a moment.";
            const suppressToast = this.shouldSuppressErrorToast(request, err);
            
            let error=err.error;

            if(err.error.error && !suppressToast){
                // Suppress toast for background switch-account calls (SIMR auto-reapply)
                const isSwitchAccount = request.url.includes('switch-account');
                if (!isSwitchAccount) {
                    this.apiService.present_toast('error',err.error.error);
                }
            }

            let errors=err.errors ?? err.error.errors;
            if(errors && !suppressToast){
                if(errors[0].message.includes('nknown')){
                    errormsg="Something went wrong, Please wait for a moment.";
                }
                else if(errors[0].message.includes('Token expired.')){
                    errormsg="Session Expired.";

                }

                
                else{
                    errormsg=errors[0].message;
                }
             this.apiService.present_toast('error',errormsg);
            }

            if (err.status === 401 && this.apiService.is_autologout && !window.location.href.includes('/login')) {
                // auto logout if 401 response returned from api
                this.apiService.storage_clear();
                location.href = "/login";
            }
            return throwError(error);
    }

    /** One renewal for an expired login token. A second failure still shows the existing banner. */
    private shouldRefreshExpiredToken(request: HttpRequest<any>, message: unknown): boolean {
        if (request.context.get(RETRIED_AFTER_REFRESH)) return false;
        if (typeof message !== 'string' || !message.includes('Token expired.')) return false;
        return !request.url.includes('/auth/refresh');
    }

    /** Optional background requests that may 403 for limited roles — do not show a global toast. */
    private shouldSuppressErrorToast(request: HttpRequest<any>, err: any): boolean {
        const url = request.url || '';
        if (request.context.get(SILENT_HTTP_ERROR)) return true;
        if (request.headers?.get('X-Suppress-Error-Toast') === '1') return true;
        // Asset-preview enrichment: linked Audio ISRC may be missing, unmapped, or taken down.
        if (err?.status === 404 && url.includes('/my-api/audio-isrc/')) return true;
        // Youtube duration lookup is retried by the form; mid-attempt 404/502 must not flash error toasts.
        if (url.includes('/my-api/tips-video-duration-from-link')) return true;
        if (url.includes('/my-api/tips-video-youtube-quality')) return true;
        if (url.includes('/my-api/tips-video-youtube-metrics')) return true;
        // Product/track pages look up mapped Video ISRCs in the background.
        if (url.includes('/my-api/tips-video-library-list') && url.includes('background=1')) return true;
        if (err?.status !== 403) return false;
        if (url.includes('switch-account')) return true;
        // Products module prefetch — not required for Video ISRC-only roles.
        if (url.includes('/items/a_r_managers')) return true;
        // Original Label master list. Video ISRC Maker can still edit; the dropdown uses a saved value.
        if (url.includes('/items/labels')) return true;
        // Linked-audio status check on the video list. A & R Legal can still open the library.
        if (url.includes('/items/tracks')) return true;
        // Hold-option check on Edit Product. The product page still loads.
        if (url.includes('/items/ddex_logs')) return true;
        // YouTube counts are saved in the background. Video ISRC edits go through my-api.
        if (request.method === 'PATCH' && url.includes('/items/tips_video_master/')) return true;
        // Product cover thumbnails — role may lack Directus file read access.
        if (url.includes('/assets/')) return true;
        return false;
    }
}
